import { defineComponent as _defineComponent } from 'vue';
import { toDisplayString as _toDisplayString, createTextVNode as _createTextVNode, unref as _unref, createVNode as _createVNode, withCtx as _withCtx, openBlock as _openBlock, createBlock as _createBlock } from "vue";
import { shallowRef, ref, computed } from "vue";
import gql from "fraql";
import { cloneDeep } from "lodash-es";
import { apolloClient } from "@/plugins/apollo/index.js";
import { useQuery } from "@vue/apollo-composable";
import { setDefaultValuesFromFormField } from "@/utils/inputs/set_default_values";
import { useFormValidation } from "@/composables/form/form_validation";
import { SentryCaptureError } from "@/utils/sentryErrorHandler";
import { injectSnackbar } from "@/composables/injections";
import AppFormFieldContainer from "@elements/formFields/AppFormFieldContainer.vue";
import { createMutationArguments } from "@elements/formFields/utils";
import AppFormDialog from "@elements/AppFormDialog.vue";
export default /*@__PURE__*/ _defineComponent({
    __name: 'EventStaffFunctionForm',
    props: {
        event: {
            type: Object,
            required: true
        }
    },
    setup(__props, { expose: __expose }) {
        const props = __props;
        const formDialog = shallowRef();
        const initForm = ref({
            vuelidateValidations: {},
            readOnlyFields: [],
            formFields: []
        });
        const showModal = ref(false);
        // its shady that form.id value needs to be own variable or it will loop query
        const formId = ref(null);
        const form = ref({
            id: null,
            eventId: props.event.id,
            startDate: null,
            eventStaffFunctionDefinitionId: null
        });
        const startDate = ref(null);
        const formSubmitted = ref(false);
        const { snackbar } = injectSnackbar();
        const { v$, fieldDisabled, fieldErrorMessages, formValidate, formErrorMessages } = useFormValidation(initForm, form);
        const loading = computed(() => initCreateFormLoading.value || initUpdateFormLoading.value);
        const { onResult, loading: initCreateFormLoading } = useQuery(gql `
    query initEventStaffFunctionCreateForm(
      $eventId: ID!
      $startDate: Date
      $eventStaffFunctionDefinitionId: ID
    ) {
      initEventStaffFunctionCreateForm(
        eventId: $eventId
        startDate: $startDate
        eventStaffFunctionDefinitionId: $eventStaffFunctionDefinitionId
      ) {
        eventStaffFunction {
          description
          startDate
          profQualificationIds
          regionIds
          licenceCategoryIds
        }
        vuelidateValidations
        readOnlyFields
        formFields {
          ${AppFormFieldContainer.fragments.formFieldFields}
        }
      }
    }
  `, () => ({
            eventId: props.event.id,
            startDate: startDate.value,
            eventStaffFunctionDefinitionId: form.value.eventStaffFunctionDefinitionId
        }), () => ({
            enabled: showModal.value && !formId.value
        }));
        onResult(({ loading, data }) => {
            if (loading)
                return;
            if (data.initEventStaffFunctionCreateForm.eventStaffFunction) {
                form.value = cloneDeep(data.initEventStaffFunctionCreateForm.eventStaffFunction); // clone to remove read-only state
            }
            form.value = setDefaultValuesFromFormField(form.value, data.initEventStaffFunctionCreateForm.formFields, {
                validationObject: v$.value.form,
                forceReturn: true,
                overwriteFields: ["name", "description", "profQualificationIds"]
            });
            initForm.value = data.initEventStaffFunctionCreateForm;
            formDialog.value.saveMetaSetOriginalForm(form.value);
        });
        const { onResult: onUpdateResult, loading: initUpdateFormLoading } = useQuery(gql `
    query initEventStaffFunctionUpdateForm($id: ID!) {
      initEventStaffFunctionUpdateForm(id: $id) {
       eventStaffFunction {
          id
          name
          description
          startDate
          profQualificationIds
          regionIds
          licenceCategoryIds
        }
        vuelidateValidations
        readOnlyFields
        formFields {
          ${AppFormFieldContainer.fragments.formFieldFields}
        }
      }
    }
  `, () => ({
            id: formId.value
        }), () => ({
            enabled: showModal.value && !!formId.value
        }));
        onUpdateResult(({ loading, data }) => {
            if (loading)
                return;
            form.value = cloneDeep(data.initEventStaffFunctionUpdateForm.eventStaffFunction); // clone to remove read-only state
            formDialog.value.saveMetaSetOriginalForm(form.value);
            initForm.value = data.initEventStaffFunctionUpdateForm;
        });
        function openForm(day = null, staff) {
            initForm.value = {
                vuelidateValidations: {},
                readOnlyFields: [],
                formFields: []
            };
            startDate.value = day;
            form.value = cloneDeep(staff ?? {
                id: null,
                eventId: props.event.id,
                startDate: day,
                eventStaffFunctionDefinitionId: null
            }); // clone to remove read-only state
            formId.value = form.value.id;
            formDialog.value.saveMetaSetOriginalForm(form.value);
            formDialog.value.closeFormFeedback();
            v$.value.$reset();
            showModal.value = true;
        }
        async function submit() {
            form.value.eventId = props.event.id;
            if (!formValidate()) {
                formDialog.value.openFormFeedback(formErrorMessages());
                return;
            }
            const { args, variables } = createMutationArguments(initForm.value);
            const create = () => {
                return apolloClient.mutate({
                    mutation: gql `
        mutation eventStaffFunctionCreate(${variables}) {
          eventStaffFunctionCreate(input: { ${args} }) {
            eventStaffFunction {
              id
              name
              description
              startDate
              profQualificationIds
              regionIds
              licenceCategoryIds
            }
          }
        }
      `,
                    variables: form.value,
                    refetchQueries: ["eventStaffFunctions"]
                });
            };
            const update = () => {
                return apolloClient.mutate({
                    mutation: gql `
        mutation eventStaffFunctionUpdate(${variables}) {
          eventStaffFunctionUpdate(input: { ${args} }) {
            eventStaffFunction {
              id
              name
              description
              profQualificationIds
              regionIds
              licenceCategoryIds
            }
          }
        }
      `,
                    variables: form.value
                });
            };
            formSubmitted.value = true;
            const submit = form.value.id ? update : create;
            // Mutation
            await submit()
                .then(() => {
                formDialog.value.saveMetaSetOriginalForm(form.value);
                showModal.value = false;
                snackbar("success", {});
                formSubmitted.value = false;
            })
                .catch(error => {
                formDialog.value.openFormFeedback(error, "serverError");
                SentryCaptureError(error, { level: "error" });
                formSubmitted.value = false;
            });
        }
        __expose({
            openForm
        });
        return (_ctx, _cache) => {
            return (_openBlock(), _createBlock(AppFormDialog, {
                ref_key: "formDialog",
                ref: formDialog,
                modelValue: showModal.value,
                "onUpdate:modelValue": _cache[1] || (_cache[1] = ($event) => ((showModal).value = $event)),
                form: form.value,
                "init-form-loading": loading.value,
                loading: formSubmitted.value,
                "data-test": "eventStaffFunctionForm",
                onSubmitForm: submit
            }, {
                header: _withCtx(() => [
                    _createTextVNode(_toDisplayString(_ctx.$t("events:staffRecruitment.eventStaffFunctions.formFields.heading", "Add/ edit a function to plan and recruit staff")), 1)
                ]),
                default: _withCtx(() => [
                    _createVNode(AppFormFieldContainer, {
                        modelValue: form.value,
                        "onUpdate:modelValue": _cache[0] || (_cache[0] = ($event) => ((form).value = $event)),
                        "form-fields": initForm.value.formFields,
                        "validation-props": {
                            fieldDisabled: _unref(fieldDisabled),
                            fieldErrors: _unref(fieldErrorMessages),
                            validationObject: _unref(v$)
                        }
                    }, null, 8, ["modelValue", "form-fields", "validation-props"])
                ]),
                _: 1
            }, 8, ["modelValue", "form", "init-form-loading", "loading"]));
        };
    }
});
